import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';
import { 
  CheckCircle2, Circle, Clock, Users, Server, 
  Zap, Brain, ArrowRight, TrendingUp, AlertCircle,
  Sparkles, Target, PlayCircle, Plus, List
} from 'lucide-react';
import { MetricCard } from '../../components/MetricCard';

/**
 * Onboarding Status - Platform Admin
 * 
 * Following 00-READ-THIS-FIRST:
 * - NO MOCK DATA: Real /api/platform-admin/tenants/onboarding
 * - NULL SAFETY: Handles missing data
 * - PROGRESSIVE COMPLEXITY: Beautiful onboarding tracker
 */

interface TenantOnboarding {
  tenant_id: string;
  tenant_name: string;
  created_at: string;
  onboarding_complete: boolean;
  completion_percentage: number;
  steps_completed: string[];
  steps_remaining: string[];
  next_action: string | null;
}

const STEP_ICONS: Record<string, React.ReactNode> = {
  users_created: <Users className="h-5 w-5" />,
  connectors_configured: <Server className="h-5 w-5" />,
  alerts_received: <Zap className="h-5 w-5" />,
  ml_enabled: <Brain className="h-5 w-5" />,
};

const STEP_LABELS: Record<string, string> = {
  users_created: 'Users Created',
  connectors_configured: 'Connectors Configured',
  alerts_received: 'Alerts Received',
  ml_enabled: 'ML Features Enabled',
};

const STEP_DESCRIPTIONS: Record<string, string> = {
  users_created: 'Add team members and assign roles',
  connectors_configured: 'Connect SIEM and data sources',
  alerts_received: 'Verify alert ingestion is working',
  ml_enabled: 'Enable machine learning capabilities',
};

export const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantOnboarding[]>([]);
  const [filter, setFilter] = useState<'all' | 'incomplete' | 'complete'>('all');

  useEffect(() => {
    fetchOnboarding();
  }, []);

  const fetchOnboarding = async () => {
    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await fetch(`${getBaseUrl()}/api/platform-admin/tenants/onboarding`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch onboarding status');
      
      const data = await response.json();
      setTenants(data.tenants || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching onboarding status:', err);
      setError(err instanceof Error ? err.message : 'Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = tenants.filter(tenant => {
    if (filter === 'all') return true;
    if (filter === 'complete') return tenant.onboarding_complete;
    if (filter === 'incomplete') return !tenant.onboarding_complete;
    return true;
  });

  const stats = {
    total: tenants.length,
    complete: tenants.filter(t => t.onboarding_complete).length,
    incomplete: tenants.filter(t => !t.onboarding_complete).length,
    avgCompletion: tenants.length > 0 
      ? Math.round(tenants.reduce((sum, t) => sum + t.completion_percentage, 0) / tenants.length) 
      : 0,
  };

  const getCompletionColor = (percentage: number) => {
    if (percentage === 100) return 'text-green-400 bg-green-500/20';
    if (percentage >= 75) return 'text-blue-400 bg-blue-500/20';
    if (percentage >= 50) return 'text-yellow-400 bg-yellow-500/20';
    return 'text-red-400 bg-red-500/20';
  };

  const getDaysOld = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-12 w-12 animate-spin border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-400">Loading onboarding status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-400 mb-2">Error Loading Onboarding Data</h3>
          <p className="text-red-400/80">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header with Action Buttons */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white mb-2">Tenant Onboarding</h1>
          <p className="text-gray-400">Track onboarding progress and guide new tenants to success</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/platform-admin/tenants/list')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors border border-gray-700 hover:border-gray-600"
          >
            <List className="h-4 w-4" />
            View All Tenants
          </button>
          <button
            onClick={() => navigate('/platform-admin/tenants/create')}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create New Tenant
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 rounded-lg p-6 border border-blue-500/20">
          <div className="flex items-center justify-between mb-2">
            <Target className="h-8 w-8 text-blue-400" />
            <span className="text-3xl font-semibold text-blue-400">{stats.avgCompletion}%</span>
          </div>
          <p className="text-sm text-gray-400">Average Completion</p>
          <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
            <div 
              className="h-full bg-blue-400 rounded-full transition-all duration-500"
              style={{ width: `${stats.avgCompletion}%` }}
            />
          </div>
        </div>

        <MetricCard
          title="Fully Onboarded"
          value={stats.complete}
          icon={CheckCircle2}
          status="success"
          description="Ready for production"
          statusLabel="Complete"
        />
        
        <MetricCard
          title="In Progress"
          value={stats.incomplete}
          icon={Clock}
          status="warning"
          description="Need attention"
          statusLabel="Active"
        />
        
        <MetricCard
          title="Total Tenants"
          value={stats.total}
          icon={Sparkles}
          status="info"
          description="Being tracked"
          statusLabel="Monitoring"
        />
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {(['all', 'incomplete', 'complete'] as const).map((filterOption) => (
          <button
            key={filterOption}
            onClick={() => setFilter(filterOption)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === filterOption
                ? 'bg-primary text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            {filterOption.charAt(0).toUpperCase() + filterOption.slice(1)}
          </button>
        ))}
      </div>

      {/* Onboarding Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredTenants.map((tenant) => {
          const daysOld = getDaysOld(tenant.created_at);
          const isStale = daysOld > 7 && !tenant.onboarding_complete;
          
          return (
            <div
              key={tenant.tenant_id}
              className={`bg-gray-800 rounded-lg border p-6 transition-all hover:shadow-lg cursor-pointer ${
                tenant.onboarding_complete 
                  ? 'border-green-500/30 hover:border-green-500/50' 
                  : isStale
                  ? 'border-red-500/30 hover:border-red-500/50'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
              onClick={() => navigate(`/platform-admin/tenants/${tenant.tenant_id}`)}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-white hover:text-primary transition-colors">{tenant.tenant_name}</h3>
                    {tenant.onboarding_complete && (
                      <CheckCircle2 className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>Created {daysOld} days ago</span>
                    {isStale && (
                      <span className="flex items-center gap-1 text-red-400">
                        <AlertCircle className="h-3 w-3" />
                        Needs attention
                      </span>
                    )}
                  </div>
                </div>
                
                <div className={`px-3 py-1.5 rounded-full text-sm font-semibold ${getCompletionColor(tenant.completion_percentage)}`}>
                  {tenant.completion_percentage}%
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      tenant.onboarding_complete 
                        ? 'bg-green-500' 
                        : 'bg-blue-500'
                    }`}
                    style={{ width: `${tenant.completion_percentage}%` }}
                  />
                </div>
              </div>

              {/* Onboarding Steps */}
              <div className="space-y-3 mb-6">
                {Object.keys(STEP_LABELS).map((step) => {
                  const isCompleted = tenant.steps_completed.includes(step);
                  const isRemaining = tenant.steps_remaining.includes(step);
                  
                  return (
                    <div
                      key={step}
                      className={`flex items-start gap-3 p-3 rounded-lg transition-colors ${
                        isCompleted 
                          ? 'bg-green-500/20 border border-green-500/30' 
                          : 'bg-gray-700/50 border border-gray-600'
                      }`}
                    >
                      <div className={`mt-0.5 ${isCompleted ? 'text-green-400' : 'text-gray-500'}`}>
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5" />
                        ) : (
                          <Circle className="h-5 w-5" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium ${isCompleted ? 'text-green-400' : 'text-white'}`}>
                            {STEP_LABELS[step]}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400">{STEP_DESCRIPTIONS[step]}</p>
                      </div>
                      {isCompleted && (
                        <div className="text-xs text-green-400 font-medium">
                          Done
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Next Action */}
              {tenant.next_action && !tenant.onboarding_complete && (
                <div 
                  className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 hover:bg-blue-500/20 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/platform-admin/tenants/${tenant.tenant_id}`);
                  }}
                >
                  <div className="flex items-start gap-3">
                    <PlayCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-400 mb-1">Next Step</p>
                      <p className="text-sm text-gray-300">{tenant.next_action}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-blue-400 flex-shrink-0" />
                  </div>
                </div>
              )}

              {/* Completion Badge */}
              {tenant.onboarding_complete && (
                <div className="mt-6 p-4 bg-green-500/20 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle2 className="h-5 w-5" />
                    <p className="font-medium">Onboarding Complete!</p>
                  </div>
                  <p className="text-sm text-gray-400 mt-1">Tenant is fully configured and operational</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredTenants.length === 0 && (
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-12 text-center">
          <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">No Tenants Found</h3>
          <p className="text-gray-400 mb-4">No tenants match the selected filter criteria</p>
          <button
            onClick={() => navigate('/platform-admin/tenants/create')}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Your First Tenant
          </button>
        </div>
      )}

      {/* Help Section - Only show if there are incomplete tenants */}
      {stats.incomplete > 0 && (
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-lg border border-blue-500/20 p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <AlertCircle className="h-6 w-6 text-blue-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-white mb-2">
                {stats.incomplete} {stats.incomplete === 1 ? 'Tenant Needs' : 'Tenants Need'} Attention
              </h3>
              <p className="text-gray-400 mb-4">
                Help your tenants complete their onboarding to unlock the full power of AstraPeak BRAIN.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setFilter('incomplete')}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  View Incomplete Tenants
                </button>
                <button
                  onClick={() => navigate('/platform-admin/tenants/list')}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Manage All Tenants
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Onboarding;
